import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import {
  AddIcon,
  Button,
  FlexContent,
  Form,
  FormField,
  InlineOrderEditor,
  Input,
  ModalDialog,
  Numeric,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import Visibility from "../../../../components/ui/Visibility";
import VisibilityScope from "../../../../components/ui/VisibilityScope";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminMaterialGrade } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";

interface FormBody {
  code: string;
  name: string;
  rhoK: number | null;
  rhoM: number | null;
  visibility: "public" | "internal" | "hidden" | null;
}

const AdminMaterialGradesTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "materialGrades" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useMaterialGrades(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminMaterialGrade>();
  const [itemToEdit, setItemToEdit] = useState<AdminMaterialGrade>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateVisibility = async (item: AdminMaterialGrade, _visibility: "public" | "internal" | "hidden") => {
    try {
      await adminApi.updateMaterialGrade(item.id, { visibility: _visibility });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await adminApi.updateMaterialGradeOrder(id, { order: newOrder });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminMaterialGrade>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.materialGrades.rhoK}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.materialGrades.rhoM}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.visibility}</TableHeadCell>
              <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.code}</TableCell>
              <TableCell>{item.name}</TableCell>
              <TableCell>{item.rhoK != null ? item.rhoK : i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>{item.rhoM != null ? item.rhoM : i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>
                {item.visibility != null && (
                  <Visibility
                    value={item.visibility as "public" | "internal" | "hidden"}
                    onChange={(nextVisibility) => updateVisibility(item, nextVisibility)}
                  />
                )}
              </TableCell>
              <TableCell align="center"><InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} /></TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.materialGrades.singular}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteMaterialGrade(itemToDelete.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <FormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </>
  );
};

const FormModal: FC<{ item?: AdminMaterialGrade; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();

  const defaultValues: FormBody = {
    code: item?.code ?? "",
    name: item?.name ?? "",
    rhoK: item?.rhoK ?? null,
    rhoM: item?.rhoM ?? null,
    visibility: (item?.visibility as "public" | "internal" | "hidden") ?? "hidden",
  };

  const onSubmit = () => async (formData: FormBody) => {
    const body = {
      code: formData.code,
      name: formData.name,
      rhoK: formData.rhoK,
      rhoM: formData.rhoM,
      visibility: formData.visibility ?? null,
    };
    try {
      if (item) {
        await adminApi.updateMaterialGrade(item.id, body);
      } else {
        await adminApi.insertMaterialGrade(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={item ? `${i18n.translation.common.edit} ${i18n.translation.lockCalculatorModule.admin.materialGrades.singular}` : `${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.materialGrades.singular}`}
    >
      <Form<FormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="code"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.code} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.name} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="rhoK"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.lockCalculatorModule.admin.materialGrades.rhoK} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="rhoM"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.lockCalculatorModule.admin.materialGrades.rhoM} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {() => <VisibilityScope value={field.value} onChange={(value) => field.onChange(value)} />}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminMaterialGradesTab;
